/**
 * Shared site/location filter vocabulary for library toolbars.
 *
 * The events library set the pattern: one searchable multi-select that mixes an
 * "all" sentinel, region entries, and individual sites. The sentinel rules live
 * in `multiSelectFilter`; what is site-specific is building the option list and
 * expanding a region pick back into the location ids a query can filter on.
 */

import {
  ALL_FILTER_VALUE,
  filterStorageValue,
  mergeFilterSelection,
  normalizeFilterValues,
  sanitizeFilterValues,
  splitFilterValues,
  summarizeFilterSelection,
} from './multiSelectFilter';

export const ALL_SITE_FILTER = ALL_FILTER_VALUE;
export const SITE_FILTER_REGION_PREFIX = 'region:';

export type SiteFilterRegion = {
  region_id: string;
  name: string;
  locationIds: string[];
};

export type SiteFilterSite = {
  location_id: string;
  name?: string | null;
};

export type SiteFilterOption = {
  id: string;
  label: string;
};

export const splitSiteFilterValues = splitFilterValues;
export const normalizeSiteFilterValues = normalizeFilterValues;
export const normalizeSiteFilterChange = mergeFilterSelection;
export const siteFilterStorageValue = filterStorageValue;
export const sanitizeSiteFilterValues = sanitizeFilterValues;

export function buildSiteFilterOptions(params: {
  regions: SiteFilterRegion[];
  sites: SiteFilterSite[];
  canSelectAllSites: boolean;
  allLabel?: string;
}): SiteFilterOption[] {
  const options: SiteFilterOption[] = [];
  if (params.canSelectAllSites) {
    options.push({ id: ALL_SITE_FILTER, label: params.allLabel ?? 'All sites' });
  }
  for (const region of params.regions) {
    options.push({
      id: `${SITE_FILTER_REGION_PREFIX}${region.region_id}`,
      label: `${region.name} (Region)`,
    });
  }
  for (const site of params.sites) {
    options.push({ id: site.location_id, label: site.name || 'Unnamed site' });
  }
  return options;
}

/**
 * Flattens the selection into the location ids a query should filter on.
 * `undefined` means "no location restriction".
 */
export function resolveSiteFilterLocationIds(
  values: string[],
  regions: SiteFilterRegion[],
): string[] | undefined {
  if (normalizeSiteFilterValues(values).includes(ALL_SITE_FILTER)) return undefined;
  const ids = new Set<string>();
  for (const value of values) {
    if (value.startsWith(SITE_FILTER_REGION_PREFIX)) {
      const regionId = value.slice(SITE_FILTER_REGION_PREFIX.length);
      const match = regions.find((region) => region.region_id === regionId);
      for (const locationId of match?.locationIds ?? []) ids.add(locationId);
    } else {
      ids.add(value);
    }
  }
  const next = Array.from(ids);
  return next.length > 0 ? next : undefined;
}

export function summarizeSiteFilterSelection(
  values: string[],
  options: SiteFilterOption[],
  allLabel = 'All sites',
): string {
  return summarizeFilterSelection(values, options, allLabel);
}
